/**
 * Enhanced ClinicSwitcher Component
 * Advanced multi-clinic switcher with detailed information and improved UX
 * Features: Dropdown with clinic details, role badges, access count, and visual feedback
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { multiClinicUserManager } from '../lib/multiClinicUserManager';

// Add spin animation keyframes as a style tag
const spinAnimation = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

// Inject CSS if not already present (guard for non-DOM test env)
if (typeof document !== 'undefined' && !document.querySelector('#clinic-switcher-styles')) {
  const style = document.createElement('style');
  style.id = 'clinic-switcher-styles';
  style.textContent = spinAnimation;
  document.head.appendChild(style);
}

const ClinicSwitcher = ({ user, onClinicChange, style = {}, disabled = false }) => {
  const [clinicContext, setClinicContext] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const loadClinicContext = useCallback(async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const context = await multiClinicUserManager.getClinicContext(user);
      setClinicContext(context);
    } catch (error) {
      console.error('Error loading clinic context:', error);
      setError('Failed to load clinic information');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadClinicContext();
  }, [loadClinicContext]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClinicSwitch = async (newClinicId) => {
    if (disabled) return;
    if (!newClinicId || newClinicId === clinicContext.defaultClinic || isSwitching) {
      return;
    }

    try {
      setIsSwitching(true);
      setError(null);
      setIsOpen(false); // Close dropdown immediately

      console.log(`Switching to clinic: ${newClinicId}`);
      
      const result = await multiClinicUserManager.switchUserClinic(newClinicId);
      
      console.log('Clinic switch successful:', result);

      // Update context locally
      setClinicContext(prev => ({
        ...prev,
        defaultClinic: newClinicId,
        role: result.role,
        topics: result.topics
      }));

      // Notify parent component
      if (onClinicChange) {
        onClinicChange({
          clinicId: newClinicId,
          role: result.role,
          topics: result.topics
        });
      }

      // Optional: Reload page to ensure all components get updated context
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error('Error switching clinic:', error);
      setError('Failed to switch clinic. Please try again.');
    } finally {
      setIsSwitching(false);
    }
  };

  const getCurrentClinic = () => {
    if (!clinicContext || !clinicContext.availableClinics) return null;
    return clinicContext.availableClinics.find(clinic => clinic.id === clinicContext.defaultClinic);
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'super_admin': '#ef4444', // Red
      'admin': '#f59e0b',       // Orange  
      'viewer': '#10b981',      // Green
      'operator': '#3b82f6'     // Blue
    };
    return colors[role] || '#6b7280';
  };

  if (isLoading) {
    return (
      <div style={{ ...enhancedStyles.container, ...style }}>
        <div style={enhancedStyles.loadingWrapper}>
          <div style={enhancedStyles.loadingSpinner}></div>
          <span style={enhancedStyles.loading}>Loading clinics...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...enhancedStyles.container, ...style }}>
        <div style={enhancedStyles.errorWrapper}>
          <span style={enhancedStyles.errorIcon}>⚠️</span>
          <span style={enhancedStyles.error}>{error}</span>
        </div>
      </div>
    );
  }

  // Render the switcher even for single clinic to display current clinic context
  if (!clinicContext || !clinicContext.availableClinics || clinicContext.availableClinics.length === 0) {
    return null;
  }

  const currentClinic = getCurrentClinic();

  return (
    <div style={{ ...enhancedStyles.container, ...style }} ref={dropdownRef}>
      <div style={enhancedStyles.dropdownContainer}>
        <button
          type="button"
          onClick={() => { if (!disabled) setIsOpen(!isOpen); }}
          disabled={isSwitching || disabled}
          style={{
            ...enhancedStyles.triggerButton,
            ...(isSwitching ? enhancedStyles.triggerButtonDisabled : {}),
            ...(disabled ? enhancedStyles.triggerButtonDisabled : {}),
            ...(isOpen ? enhancedStyles.triggerButtonActive : {})
          }}
          data-testid="clinic-switcher-trigger"
        >
          <div style={enhancedStyles.triggerContent}>
            <div style={enhancedStyles.triggerMain}>
              <span style={enhancedStyles.clinicIcon}>🏥</span>
              <div style={enhancedStyles.clinicInfo}>
                <span style={enhancedStyles.clinicName}>
                  {currentClinic?.name || 'Unknown Clinic'}
                </span>
                <span 
                  style={{
                    ...enhancedStyles.roleBadge,
                    backgroundColor: getRoleBadgeColor(currentClinic?.role)
                  }}
                >
                  {currentClinic?.role || 'unknown'}
                </span>
              </div>
            </div>
            <span style={{
              ...enhancedStyles.dropdownArrow,
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
            }}>
              ▼
            </span>
          </div>
          
          {isSwitching && (
            <div style={enhancedStyles.switchingOverlay}>
              <div style={enhancedStyles.switchingSpinner}></div>
              <span>Switching...</span>
            </div>
          )}
        </button>

        {isOpen && !disabled && (
          <div style={enhancedStyles.dropdown}>
            <div style={enhancedStyles.dropdownHeader}>
              <span style={enhancedStyles.dropdownTitle}>
                Select Clinic ({clinicContext.availableClinics.length})
              </span>
            </div>
            
            <div style={enhancedStyles.dropdownList}>
              {clinicContext.availableClinics.map(clinic => (
                <button
                  key={clinic.id}
                  onClick={() => handleClinicSwitch(clinic.id)}
                  disabled={clinic.id === clinicContext.defaultClinic || isSwitching || disabled}
                  style={{
                    ...enhancedStyles.dropdownItem,
                    ...(clinic.id === clinicContext.defaultClinic ? enhancedStyles.dropdownItemActive : {}),
                    ...((isSwitching || disabled) ? enhancedStyles.dropdownItemDisabled : {})
                  }}
                  data-testid={`clinic-option-${clinic.id}`}
                >
                  <div style={enhancedStyles.dropdownItemContent}>
                    <div style={enhancedStyles.dropdownItemMain}>
                      <span style={enhancedStyles.dropdownItemIcon}>
                        {clinic.id === clinicContext.defaultClinic ? '✓' : '🏥'}
                      </span>
                      <div style={enhancedStyles.dropdownItemInfo}>
                        <span style={enhancedStyles.dropdownItemName}>
                          {clinic.name}
                        </span>
                        <span style={enhancedStyles.dropdownItemId}>
                          ID: {clinic.id}
                        </span>
                      </div>
                    </div>
                    <span 
                      style={{
                        ...enhancedStyles.dropdownItemBadge,
                        backgroundColor: getRoleBadgeColor(clinic.role)
                      }}
                    >
                      {clinic.role}
                    </span>
                  </div>
                  
                  {clinic.id === clinicContext.defaultClinic && (
                    <div style={enhancedStyles.currentLabel}>Current</div>
                  )}
                </button>
              ))}
            </div>
            
            <div style={enhancedStyles.dropdownFooter}>
              <span style={enhancedStyles.dropdownFooterText}>
                Total access: {clinicContext.permissions?.totalClinics || clinicContext.availableClinics.length} clinics
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const enhancedStyles = {
  container: {
    position: 'relative',
    display: 'inline-block',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: '14px'
  },

  // Loading States
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0'
  },
  loadingSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid #e2e8f0',
    borderTop: '2px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  loading: {
    color: '#64748b',
    fontSize: '13px'
  },

  // Error States
  errorWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    backgroundColor: '#fef2f2',
    borderRadius: '6px',
    border: '1px solid #fecaca'
  },
  errorIcon: {
    fontSize: '14px'
  },
  error: {
    color: '#dc2626',
    fontSize: '12px',
    fontWeight: '500'
  },

  // Dropdown Container
  dropdownContainer: {
    position: 'relative',
    display: 'inline-block'
  },

  // Trigger Button
  triggerButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    backgroundColor: 'white',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '220px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    position: 'relative',
    ':hover': {
      borderColor: '#9ca3af',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
    }
  },
  triggerButtonActive: {
    borderColor: '#3b82f6',
    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
  },
  triggerButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },

  triggerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  },
  triggerMain: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  clinicIcon: {
    fontSize: '16px'
  },
  clinicInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px'
  },
  clinicName: {
    fontWeight: '600',
    color: '#1f2937',
    fontSize: '14px',
    lineHeight: '1.2'
  },
  roleBadge: {
    fontSize: '10px',
    fontWeight: '600',
    color: 'white',
    padding: '2px 6px',
    borderRadius: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
  },
  dropdownArrow: {
    fontSize: '10px',
    color: '#6b7280',
    transition: 'transform 0.2s ease',
    marginLeft: '8px'
  },

  // Switching Overlay
  switchingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    borderRadius: '8px',
    fontSize: '12px',
    color: '#3b82f6',
    fontWeight: '500'
  },
  switchingSpinner: {
    width: '12px',
    height: '12px',
    border: '2px solid #e5e7eb',
    borderTop: '2px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },

  // Dropdown Menu
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    backgroundColor: 'white',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    overflow: 'hidden'
  },

  // Dropdown Header
  dropdownHeader: {
    padding: '12px 16px',
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#f8fafc'
  },
  dropdownTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#374151',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },

  // Dropdown List
  dropdownList: {
    maxHeight: '240px',
    overflowY: 'auto'
  },

  // Dropdown Items
  dropdownItem: {
    width: '100%',
    padding: '12px 16px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    textAlign: 'left',
    display: 'block',
    position: 'relative',
    ':hover': {
      backgroundColor: '#f1f5f9'
    }
  },
  dropdownItemActive: {
    backgroundColor: '#eff6ff',
    borderLeft: '3px solid #3b82f6'
  },
  dropdownItemDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },

  dropdownItemContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px'
  },
  dropdownItemMain: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flex: 1
  },
  dropdownItemIcon: {
    fontSize: '14px',
    width: '16px',
    textAlign: 'center'
  },
  dropdownItemInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  dropdownItemName: {
    fontWeight: '500',
    color: '#1f2937',
    fontSize: '14px'
  },
  dropdownItemId: {
    fontSize: '11px',
    color: '#6b7280',
    fontFamily: 'monospace'
  },
  dropdownItemBadge: {
    fontSize: '9px',
    fontWeight: '600',
    color: 'white',
    padding: '2px 5px',
    borderRadius: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  
  currentLabel: {
    position: 'absolute',
    top: '6px',
    right: '12px',
    fontSize: '9px',
    fontWeight: '600',
    color: '#3b82f6',
    backgroundColor: '#eff6ff',
    padding: '2px 5px',
    borderRadius: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },

  // Dropdown Footer
  dropdownFooter: {
    padding: '8px 16px',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#f8fafc'
  },
  dropdownFooterText: {
    fontSize: '11px',
    color: '#6b7280',
    fontStyle: 'italic'
  }
};

export default ClinicSwitcher;
